import { Fragment } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import styles from './BudgetReportPrintSheet.module.scss';
import { Props } from './BudgetReportPrintSheet.types';

/**
 * The printable end-of-year report. It is portalled into `<body>` so that, once `index.css`
 * hides `#root`, nothing of the application layout (navigation, tabs, scroll containers) can
 * end up on the paper. Everything it shows is pre-formatted by `BudgetTracking`.
 */
const BudgetReportPrintSheet = ({
  assetName,
  year,
  editedOn,
  sections,
  capText,
  unplannedText,
}: Props) => {
  const { t } = useTranslation();

  return createPortal(
    <div className={`sh-print-sheet ${styles.sheet}`}>
      <div className={styles.header}>
        <h1 className={styles.title}>
          {t('budgetV2.print.title', 'Bilan budgétaire {{year}} — {{asset}}', {
            year,
            asset: assetName,
          })}
        </h1>
        <p className={styles.subtitle}>
          {t(
            'budgetV2.print.subtitle',
            'Réel constaté vs budget prévu · Édité le {{date}} · ShareHeritage',
            { date: editedOn }
          )}
        </p>
      </div>

      <p className={styles.cap}>{capText}</p>

      <table className={styles.table}>
        <thead>
          <tr>
            <th>{t('budgetV2.tracking.line', 'Ligne')}</th>
            <th>{t('budgetV2.tracking.planned', 'Prévu')}</th>
            <th>{t('budgetV2.tracking.actual', 'Réel')}</th>
            <th>{t('budgetV2.tracking.variance', 'Écart')}</th>
          </tr>
        </thead>
        <tbody>
          {sections.map(section => (
            <Fragment key={section.key}>
              <tr className={styles.sectionRow}>
                <td colSpan={4}>{section.label}</td>
              </tr>
              {section.rows.map(row => (
                <tr key={row.key}>
                  <td>{row.label}</td>
                  <td>{row.plannedText}</td>
                  <td>{row.actualText}</td>
                  <td>{row.varianceText}</td>
                </tr>
              ))}
            </Fragment>
          ))}
        </tbody>
      </table>

      {unplannedText ? (
        <div className={styles.unplanned}>
          <h2 className={styles.unplannedTitle}>
            {t('budgetV2.tracking.unplannedTitle', 'Constaté mais non prévu au budget')}
          </h2>
          <p>{unplannedText}</p>
        </div>
      ) : null}
    </div>,
    document.body
  );
};

export default BudgetReportPrintSheet;
